import React, { useState, useRef, useEffect } from 'react';
import { RiMenu3Line, RiCloseLine } from 'react-icons/ri';
import { FiBookOpen } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import './navbar.css';
import logo from '../../logo.svg';
import Modal from '../modal/modal';
import instagramLogo from '../../assets/instagram.svg';
import { useAuth } from '../../context/AuthContext';
import { useAppTheme } from '../../context/AppThemeContext';

const roleColors = {
  admin: '#AD1C00',
  teacher: '#1565c0',
  student: '#2e7d32',
  other: '#6d4c41',
};

const Navbar = () => {
  const [toggleMenu, setToggleMenu] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const { currentUser, isAuthenticated, isAdmin, logout } = useAuth();
  const { isLightTheme, toggleTheme } = useAppTheme();
  const navigate = useNavigate();

  const toggleMenuHandler = () => setToggleMenu((v) => !v);
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const handleOutsideClick = (e) => {
    if (e.target.classList.contains('modal')) closeModal();
  };

  // Close user menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
    navigate('/');
  };

  const getInitials = (name) => name ? name.slice(0, 2).toUpperCase() : 'U';
  const roleColor = currentUser ? (roleColors[currentUser.role] || '#555') : '#555';

  return (
    <nav className="gpt3__navbar" aria-label="Main Navigation">
      <div className="gpt3__navbar-links">
        <div className="gpt3__navbar-links_logo">
          <img src={logo} alt="Algodoor logo" style={{ width: '100%', height: 'auto' }} />
        </div>
        <div className="gpt3__navbar-links_container">
          <p><a href="#home">Home</a></p>
          <p><a href="#features">Products/Services</a></p>
          <p><a href="#wgpt3">What is AlgoDoor?</a></p>
          <p><a href="#contactus">Contact Us</a></p>
          <p><a href="#portfolio">Portfolio</a></p>
          <p>
            <button
              type="button"
              className="navbar-nav-link-btn"
              onClick={() => navigate('/courses')}
            >
              Courses
            </button>
          </p>
          <p>
            <button
              type="button"
              className="navbar-nav-link-btn"
              onClick={() => navigate('/problems')}
            >
              DSA Lab
            </button>
          </p>
          <p>
            <button
              type="button"
              className="navbar-nav-link-btn"
              onClick={() => navigate('/learn')}
            >
              <span className="navbar-learn-label"><FiBookOpen /> Learn</span>
            </button>
          </p>
        </div>
      </div>

      <div className="gpt3__navbar-sign">
        <img src={instagramLogo} alt="Algodoor Instagram" style={{ width: '20px', height: '20px', marginRight: '5px' }} />
        <button key="modalBtn" type="button" className="navbar-quote-btn" onClick={openModal}>Get A Quote</button>

        {/* User Auth Area */}
        <div className="navbar-auth-area" ref={userMenuRef}>
          {isAuthenticated ? (
            <button
              type="button"
              className="navbar-user-btn"
              onClick={() => setUserMenuOpen((v) => !v)}
              aria-label="User menu"
              style={{ '--role-color': roleColor }}
            >
              <span className="navbar-user-avatar" style={{ background: roleColor }}>
                {getInitials(currentUser.username)}
              </span>
              <span className="navbar-user-name">{currentUser.username}</span>
              <span className="navbar-user-chevron">▾</span>
            </button>
          ) : (
            <button
              type="button"
              className="navbar-login-btn"
              onClick={() => navigate('/login')}
              aria-label="Login"
            >
              <span className="navbar-user-icon">👤</span>
              <span>Login</span>
            </button>
          )}

          {userMenuOpen && (
            <div className="navbar-user-dropdown scale-up-center">
              {isAuthenticated && (
                <>
                  <div className="navbar-dropdown-header">
                    <div className="navbar-dropdown-avatar" style={{ background: roleColor }}>
                      {getInitials(currentUser.username)}
                    </div>
                    <div>
                      <div className="navbar-dropdown-username">{currentUser.username}</div>
                      <div className="navbar-dropdown-role" style={{ color: roleColor }}>
                        {currentUser.role}
                      </div>
                    </div>
                  </div>
                  <hr className="navbar-dropdown-divider" />
                  <button
                    type="button"
                    className="navbar-dropdown-item"
                    onClick={() => { navigate('/certificates'); setUserMenuOpen(false); }}
                  >
                    📜 Certificates
                  </button>
                  <button
                    type="button"
                    className="navbar-dropdown-item"
                    onClick={() => { navigate('/emailsender'); setUserMenuOpen(false); }}
                  >
                    ✉️ Email Sender
                  </button>
                  {isAdmin && (
                    <button
                      type="button"
                      className="navbar-dropdown-item navbar-dropdown-admin"
                      onClick={() => { navigate('/submitted-questions'); setUserMenuOpen(false); }}
                    >
                      ❓ Submitted Questions
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      type="button"
                      className="navbar-dropdown-item navbar-dropdown-admin"
                      onClick={() => { navigate('/admin'); setUserMenuOpen(false); }}
                    >
                      🛡️ Admin Panel
                    </button>
                  )}
                  <hr className="navbar-dropdown-divider" />
                  <button
                    type="button"
                    className="navbar-dropdown-item navbar-dropdown-logout"
                    onClick={handleLogout}
                  >
                    🚪 Logout
                  </button>
                </>
              )}
              {!isAuthenticated && (
                <>
                  <button
                    type="button"
                    className="navbar-dropdown-item"
                    onClick={() => { navigate('/login'); setUserMenuOpen(false); }}
                  >
                    🔐 Login
                  </button>
                  <button
                    type="button"
                    className="navbar-dropdown-item"
                    onClick={() => { navigate('/signup'); setUserMenuOpen(false); }}
                  >
                    ✍️ Sign Up
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        <button
          type="button"
          className="navbar-theme-toggle"
          onClick={toggleTheme}
          aria-label={`Switch to ${isLightTheme ? 'dark' : 'light'} theme`}
        >
          {isLightTheme ? 'Dark' : 'Light'}
        </button>
      </div>

      <div className="gpt3__navbar-menu">
        {toggleMenu
          ? <RiCloseLine color={isLightTheme ? '#0f2d4a' : '#fff'} size={27} onClick={toggleMenuHandler} />
          : <RiMenu3Line color={isLightTheme ? '#0f2d4a' : '#fff'} size={27} onClick={toggleMenuHandler} />}
        {toggleMenu && (
          <div className="gpt3__navbar-menu_container scale-up-center">
            <div className="gpt3__navbar-menu_container-links">
              <p><a href="#home">Home</a></p>
              <p><a href="#features">Products/Services</a></p>
              <p><a href="#wgpt3">What is AlgoDoor?</a></p>
              <p><a href="#contactus">Contact Us</a></p>
              <p><a href="#portfolio">Portfolio</a></p>
              <p>
                <button
                  type="button"
                  className="navbar-nav-link-btn"
                  onClick={() => { navigate('/courses'); setToggleMenu(false); }}
                >
                  Courses
                </button>
              </p>
              <p>
                <button
                  type="button"
                  className="navbar-nav-link-btn"
                  onClick={() => { navigate('/problems'); setToggleMenu(false); }}
                >
                  DSA Lab
                </button>
              </p>
              <p>
                <button
                  type="button"
                  className="navbar-nav-link-btn"
                  onClick={() => { navigate('/learn'); setToggleMenu(false); }}
                >
                  <span className="navbar-learn-label"><FiBookOpen /> Learn</span>
                </button>
              </p>
            </div>
            <div className="gpt3__navbar-menu_container-links-sign">
              <button
                type="button"
                className="navbar-menu-link-btn"
                onClick={() => {
                  toggleTheme();
                  setToggleMenu(false);
                }}
              >
                {isLightTheme ? 'Dark Theme' : 'Light Theme'}
              </button>
              <button
                key="modalBtn"
                type="button"
                className="navbar-menu-link-btn"
                onClick={openModal}
              >
                Get A Quote
              </button>
              {isAuthenticated ? (
                <>
                  <button
                    type="button"
                    className="navbar-menu-link-btn"
                    onClick={() => { navigate('/certificates'); setToggleMenu(false); }}
                  >
                    Certificates
                  </button>
                  <button
                    type="button"
                    className="navbar-menu-link-btn"
                    onClick={() => { navigate('/emailsender'); setToggleMenu(false); }}
                  >
                    Email Sender
                  </button>
                  {isAdmin && (
                    <button
                      type="button"
                      className="navbar-menu-link-btn"
                      onClick={() => { navigate('/submitted-questions'); setToggleMenu(false); }}
                    >
                      Submitted Questions
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      type="button"
                      className="navbar-menu-link-btn"
                      onClick={() => { navigate('/admin'); setToggleMenu(false); }}
                    >
                      Admin Panel
                    </button>
                  )}
                  <button type="button" className="navbar-menu-link-btn" onClick={handleLogout}>
                    Logout ({currentUser.username})
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="navbar-menu-link-btn"
                  onClick={() => navigate('/login')}
                >
                  Login / Sign Up
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="modal-overlay" onClick={handleOutsideClick}>
          <Modal closeModal={closeModal} />
        </div>
      )}
    </nav>
  );
};

export default Navbar;
